// Hotspot overlay — annotates panel controls with click-to-explain badges.
//
// Active only when overlay === "hotspot". Each hotspot resolves to a
// live panel element via its `targetSelector`. The layer measures the
// target's bounding rect on mount and on every resize/scroll, then
// renders a badge over the element using the *measured* rect — no
// hand-maintained bbox values to drift when the panel layout changes.
//
// The layer sits as a sibling of the scaled surface inside the
// HelpShell's `position: relative` wrapper; rects are translated into
// that wrapper's coordinate space.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHelp } from "./help-context";
import { getHelpData } from "./help-data";
import type { Hotspot } from "./help-types";

interface MeasuredRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function HotspotLayer() {
  const { panelKind, overlay, selectedHotspotId, selectHotspot, closeOverlay } = useHelp();
  const data = getHelpData(panelKind);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const [rects, setRects] = useState<Record<string, MeasuredRect>>({});
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  const measure = useCallback(() => {
    const layer = layerRef.current;
    if (!layer) return;
    const container = layer.parentElement;
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    setContainerSize({ w: containerRect.width, h: containerRect.height });

    const next: Record<string, MeasuredRect> = {};
    for (const h of data.hotspots) {
      const el = container.querySelector<HTMLElement>(h.targetSelector);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      next[h.id] = {
        x: r.left - containerRect.left,
        y: r.top - containerRect.top,
        w: r.width,
        h: r.height,
      };
    }
    setRects(next);
  }, [data.hotspots]);

  useLayoutEffect(() => {
    if (overlay !== "hotspot") return;
    measure();
  }, [overlay, measure]);

  useEffect(() => {
    if (overlay !== "hotspot") return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    const layer = layerRef.current;
    const container = layer?.parentElement;
    let ro: ResizeObserver | null = null;
    if (container && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(container);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
      ro?.disconnect();
    };
  }, [overlay, measure]);

  useEffect(() => {
    if (overlay !== "hotspot") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeOverlay();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [overlay, closeOverlay]);

  if (overlay !== "hotspot") return null;
  if (data.hotspots.length === 0) return null;

  const selected = data.hotspots.find((h) => h.id === selectedHotspotId) ?? null;
  const selectedRect = selected ? rects[selected.id] : null;

  return (
    <div
      ref={layerRef}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 25,
        pointerEvents: "none",
      }}
      aria-label="Hotspot annotations"
    >
      {data.hotspots.map((h) => {
        const r = rects[h.id];
        if (!r) return null;
        return (
          <HotspotBadge
            key={h.id}
            hotspot={h}
            rect={r}
            selected={h.id === selectedHotspotId}
            onSelect={() => selectHotspot(h.id === selectedHotspotId ? null : h.id)}
          />
        );
      })}
      {selected && selectedRect && (
        <HotspotPopover
          hotspot={selected}
          rect={selectedRect}
          containerSize={containerSize}
          onClose={() => selectHotspot(null)}
        />
      )}
    </div>
  );
}

function HotspotBadge({
  hotspot,
  rect,
  selected,
  onSelect,
}: {
  hotspot: Hotspot;
  rect: MeasuredRect;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={hotspot.ariaLabel ?? `${hotspot.label} — ${hotspot.body}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      style={{
        position: "absolute",
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        zIndex: 26,
        border: `2px solid ${selected ? "var(--orange-deep, #b8651b)" : "var(--orange, #d97a1f)"}`,
        background: selected ? "rgba(217, 122, 31, 0.22)" : "rgba(217, 122, 31, 0.12)",
        borderRadius: 4,
        cursor: "pointer",
        padding: 0,
        pointerEvents: "auto",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: -10,
          left: -10,
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "var(--orange-deep, #b8651b)",
          color: "var(--white, #ffffff)",
          fontSize: 12,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 4px rgba(27, 22, 10, 0.3)",
        }}
      >
        ?
      </span>
    </button>
  );
}

function HotspotPopover({
  hotspot,
  rect,
  containerSize,
  onClose,
}: {
  hotspot: Hotspot;
  rect: MeasuredRect;
  containerSize: { w: number; h: number };
  onClose: () => void;
}) {
  const popoverWidth = 280;
  const margin = 16;

  // Anchor below the badge by default; flip above if it would go out of bounds.
  const idealLeft = rect.x + rect.w / 2 - popoverWidth / 2;
  const left = Math.max(margin, Math.min(containerSize.w - popoverWidth - margin, idealLeft));
  const top = rect.y + rect.h + 12;
  const flipUp = top + 200 > containerSize.h;

  return (
    <div
      role="dialog"
      aria-label={hotspot.label}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        left,
        top: flipUp ? "auto" : top,
        bottom: flipUp ? containerSize.h - rect.y + 12 : "auto",
        width: popoverWidth,
        zIndex: 27,
        background: "var(--white, #ffffff)",
        border: "1px solid var(--rule, #d8d3c7)",
        borderRadius: 8,
        padding: "12px 14px",
        boxShadow: "0 8px 24px rgba(27, 22, 10, 0.18)",
        pointerEvents: "auto",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--muted, #7a7363)",
          }}
        >
          {hotspot.label}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close annotation"
          style={{
            border: "none",
            background: "transparent",
            color: "var(--muted, #7a7363)",
            cursor: "pointer",
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--ink, #1b160a)" }}>
        {hotspot.body}
      </p>
    </div>
  );
}
